import { useMemo, useState } from "react";
import POKEMON_SETS_INDEX from "@/data/pokemonSetsIndex.json";

interface IndexedSet {
  id: string;
  name: string;
  series?: string;
  releaseDate?: string;
  ptcgoCode?: string;
}

// Newest sets first so the empty-query list and ties surface recent releases.
const SETS = (POKEMON_SETS_INDEX as IndexedSet[])
  .slice()
  .sort((a, b) => (b.releaseDate ?? "").localeCompare(a.releaseDate ?? ""));

const MAX_RESULTS = 10;

/**
 * Searchable Set-name input for Pokémon cards. Filters the static set catalog
 * by name or PTCGO code; picking a match calls `onSelectSet(name, code)` so the
 * caller can backfill the Set Code. Free typing still flows through `onChange`,
 * so unknown/custom sets work — this is a suggest box, not a hard select.
 */
export function SetCombobox({
  label = "Set Name",
  value,
  onChange,
  onSelectSet,
  placeholder = "Search sets…",
}: {
  label?: string;
  value: string;
  onChange: (name: string) => void;
  onSelectSet: (name: string, code: string, id: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);

  const matches = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return SETS.slice(0, MAX_RESULTS);
    return SETS.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.ptcgoCode ?? "").toLowerCase().includes(q),
    ).slice(0, MAX_RESULTS);
  }, [value]);

  return (
    <div className="mb-4 flex flex-col gap-1.5">
      <label className="text-xs font-medium tracking-wide text-muted-foreground">
        {label}
      </label>
      <div className="relative">
        <div className="flex items-center rounded-md border border-border bg-card px-3 focus-within:border-primary">
          <input
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            // Delay close so a click on an option (onMouseDown) lands first.
            onBlur={() => setTimeout(() => setOpen(false), 120)}
            placeholder={placeholder}
            className="min-h-[44px] w-full bg-transparent py-3 text-[15px] text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
        </div>
        {open && matches.length > 0 && (
          <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-card shadow-lg">
            {matches.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelectSet(s.name, s.ptcgoCode ?? "", s.id);
                    setOpen(false);
                  }}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-muted"
                >
                  <span className="truncate">{s.name}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {s.ptcgoCode ?? ""}
                    {s.releaseDate ? ` · ${s.releaseDate.slice(0, 4)}` : ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
